import { Alert, Badge, Box, Button, Group, Modal, Paper, Stack, Text } from "@mantine/core";
import { IconArrowBack, IconHistory, IconNotes, IconPencil } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useSWR, { mutate } from "swr";
import { TenantFeePolicyApi, TenantFeePolicyAssignmentApi, TradingRelationshipApi } from "../../../apis";
import type { TradingRelationshipOption } from "../../../apis/tradingRelationship";
import type { FeePolicyTier } from "../../../apis/tenantFeePolicy";
import { ConflictReloadModal } from "../../../components/billing/ConflictReloadModal";
import { DiffViewer } from "../../../components/billing/DiffViewer";
import { FeePreviewSimulator } from "../../../components/billing/FeePreviewSimulator";
import { PermissionGate } from "../../../components/billing/PermissionGate";
import { PolicyForm, type PolicyFormValue } from "../../../components/billing/PolicyForm";
import { useOptimisticMutation } from "../../../hooks/useOptimisticMutation";
import { resolveBillingError } from "../../../utils/billing-errors";
import type { FeeTier } from "../../../utils/fee-preview";
import { Message } from "../../../utils/message";

// Relative "Хадгалсан: N минутын өмнө" label, shared by the header strip and the
// footer (spec §3.3 / mockup D16). Returns null when nothing has been saved yet.
function formatSavedAgo(lastSavedAt?: string | null): string | null {
  if (!lastSavedAt) return null;
  const diffMin = Math.max(0, Math.round((Date.now() - new Date(lastSavedAt).getTime()) / 60000));
  if (diffMin < 1) return "Хадгалсан: дөнгөж сая";
  if (diffMin < 60) return `Хадгалсан: ${diffMin} минутын өмнө`;
  const h = Math.floor(diffMin / 60);
  return `Хадгалсан: ${h} цагийн өмнө`;
}

function DraftHeaderStrip({
  scope,
  name,
  version,
  rowVersion,
  lastSavedAt,
  saving,
}: {
  scope: "DEFAULT" | "NAMED" | undefined;
  name?: string | null;
  version?: number;
  rowVersion?: number;
  lastSavedAt?: string | null;
  saving?: boolean;
}) {
  // Re-render once a minute so the relative "N минутын өмнө" label stays fresh
  // even when the user isn't editing.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const savedAgo = formatSavedAgo(lastSavedAt);
  const scopeLabel = scope === "NAMED" ? "Нэрлэсэн" : scope === "DEFAULT" ? "Үндсэн" : "—";
  return (
    <Paper p="sm" withBorder radius="md" mb="md" style={{ background: "light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))" }}>
      <Group justify="space-between" wrap="wrap" gap="xs">
        <Group gap="xs">
          <Badge color="gray" leftSection={<IconNotes size={12} />}>Ноорог</Badge>
          <Text fw={600}>
            {scopeLabel}
            {Number.isFinite(version) && (version as number) > 0 ? ` v${version}` : ""}
            {name ? ` · ${name}` : ""}
          </Text>
          {saving
            ? <Text size="xs" c="dimmed">Хадгалж байна…</Text>
            : savedAgo && <Text size="xs" c="dimmed">{savedAgo}</Text>}
        </Group>
        {rowVersion !== undefined && (
          <Text size="xs" c="dimmed">хувилбар: {rowVersion}</Text>
        )}
      </Group>
    </Paper>
  );
}

const FeePolicyDraftPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !id;
  // A new draft can be opened pre-scoped (e.g. "Шинэ NAMED policy" → ?scope=NAMED).
  // Only honored for new drafts; an existing draft's scope comes from the server.
  const initialScope: "DEFAULT" | "NAMED" = searchParams.get("scope") === "NAMED" ? "NAMED" : "DEFAULT";
  const { data: loaded } = useSWR(id ? `swr.my.fee-policy.${id}` : null, () => (id ? TenantFeePolicyApi.get(id) : null));
  // Current ACTIVE policies — used to derive the "before" side of the activate diff.
  const { data: activeList } = useSWR(id ? "swr.my.fee-policy.active-list" : null, () => TenantFeePolicyApi.list({ status: "ACTIVE" }));
  // All policies of this tenant — used for client-side NAMED name uniqueness (spec §3.3).
  const { data: allList } = useSWR("swr.my.fee-policy.all-for-uniqueness", () => TenantFeePolicyApi.list({ page: 1, limit: 200 }));
  // B8 — the tenant's trading relationships, for the "exclude TRs" picker.
  const { data: trList } = useSWR<TradingRelationshipOption[]>(
    "swr.my.trading-relationships",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => TradingRelationshipApi.list() as any,
    { revalidateOnFocus: false }
  );
  // Amend: the source policy id is carried on the loaded draft. Read it from the
  // raw loaded record so the amend banner/version copy can render before the form
  // state below is hydrated.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sourcePolicyId: string | null = (loaded as any)?.sourcePolicyId ?? null;
  const isAmend = !!sourcePolicyId;
  // NAMED inline customer assignment (spec §3.3). Roster of this tenant's end-customers
  // for the picker, plus the policy's existing active assignments (edit/amend) so the
  // selection reflects reality and we can diff add/remove on activation.
  const { data: customerRoster } = useSWR(
    "swr.my.fee-policy-customers.roster",
    () => TenantFeePolicyAssignmentApi.listCustomers({ page: 1, limit: 500 }),
    { revalidateOnFocus: false }
  );
  // Flat, deduped customer list (the roster can repeat a business). PolicyForm builds
  // the grouped add-picker and the colored type badges from this.
  const customers = (() => {
    const seen = new Set<string>();
    const out: { value: string; name: string; regNumber: string | null; type: string }[] = [];
    for (const c of customerRoster?.rows ?? []) {
      if (seen.has(c.businessId)) continue;
      seen.add(c.businessId);
      out.push({ value: c.businessId, name: c.name ?? c.businessId, regNumber: c.regNumber, type: c.type });
    }
    return out;
  })();
  const assignmentPolicyId = sourcePolicyId ?? id ?? null;
  const { data: existingAssignments } = useSWR(
    assignmentPolicyId ? `swr.my.fee-policy-assignments.${assignmentPolicyId}` : null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => TenantFeePolicyAssignmentApi.list({ feePolicyId: assignmentPolicyId!, limit: 1000 }) as any,
    { revalidateOnFocus: false }
  );
  // Source policy — only to show the next version number (v{n+1}) in the amend copy.
  const { data: sourcePolicy } = useSWR(
    sourcePolicyId ? `swr.my.fee-policy.${sourcePolicyId}` : null,
    () => (sourcePolicyId ? TenantFeePolicyApi.get(sourcePolicyId) : null)
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nextVersion: number = ((sourcePolicy as any)?.currentVersion ?? 1) + 1;

  const [form, setForm] = useState<PolicyFormValue>({
    scope               : initialScope,
    calculationMethod   : "PERCENTAGE",
    feeBasis            : "ORDER_TOTAL",
    deductionMode       : "SEPARATE_INVOICE",
    refundPolicy        : "PRO_RATA",
    feeCurrency         : "MNT",
    absorbPlatformFee   : false,
    chargeEndCustomerFee: true,
    applicableOrderTypes: [],
    applicableCurrencies: [],
    excludedTradingRelationshipIds: [],
    effectiveUntil      : null
  });
  const [effectiveFrom, setEffectiveFrom] = useState<string | null>(null);
  // NAMED target customers (business ids). Not part of the policy body — applied via
  // assignBulk/unassign on activation. originalAssignments is the server baseline used
  // to diff added/removed customers on activate.
  const [targetCustomerIds, setTargetCustomerIds] = useState<string[]>([]);
  const originalAssignments = useRef<{ id: string; businessId: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { conflict, clearConflict, run } = useOptimisticMutation();

  // Auto-save (spec §3.3): debounced 2s after the last change, only for an
  // existing (already-saved) draft that has unsaved edits.
  const [dirty, setDirty] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelAutoSave = useCallback(() => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
  }, []);

  useEffect(() => {
    if (loaded) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const l: any = loaded;
      setForm({
        scope            : l.scope,
        name             : l.name,
        calculationMethod: l.calculationMethod,
        feeRate          : l.feeRate,
        flatFeeAmount    : l.flatFeeAmount,
        tierConfig       : l.tierConfig?.tiers ?? (Array.isArray(l.tierConfig) ? l.tierConfig : null),
        minFeeAmount     : l.minFeeAmount,
        maxFeeAmount     : l.maxFeeAmount,
        feeBasis         : l.feeBasis,
        deductionMode    : l.deductionMode,
        refundPolicy     : l.refundPolicy,
        absorbPlatformFee   : l.absorbPlatformFee,
        chargeEndCustomerFee: l.chargeEndCustomerFee ?? true,
        feeCurrency         : l.feeCurrency,
        applicableOrderTypes: l.applicableOrderTypes ?? [],
        applicableCurrencies: l.applicableCurrencies ?? [],
        excludedTradingRelationshipIds: l.excludedTradingRelationshipIds ?? [],
        effectiveUntil      : l.effectiveUntil ? String(l.effectiveUntil).substring(0, 10) : null,
        rowVersion          : l.rowVersion
      });
      if (l.effectiveFrom) setEffectiveFrom(String(l.effectiveFrom).substring(0, 10));
      if (l.updatedAt) setLastSavedAt(String(l.updatedAt));
      // Freshly (re)loaded server state is the new baseline — nothing to auto-save yet.
      setDirty(false);
    }
  }, [loaded]);

  const updateField = useCallback((k: keyof PolicyFormValue, v: unknown) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setDirty(true);
  }, []);

  const onEffectiveFromChange = useCallback((v: string | null) => {
    setEffectiveFrom(v);
    setDirty(true);
  }, []);

  // Create-flow handoff: a new draft saves first (gets an id) then activates on a fresh
  // mount, so the customers picked at create time are stashed under the new id and
  // rehydrated here. Consumed once.
  useEffect(() => {
    if (!id) return;
    const raw = sessionStorage.getItem(`fp-pending-customers:${id}`);
    if (raw) {
      try { setTargetCustomerIds(JSON.parse(raw)); } catch { /* ignore */ }
      sessionStorage.removeItem(`fp-pending-customers:${id}`);
    }
  }, [id]);

  // Seed the picker + diff baseline from the policy's existing active assignments.
  // An empty server result never clobbers a create-flow rehydrated selection.
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rows = (existingAssignments as any)?.rows as Array<{ id: string; endCustomerBusinessId: string }> | undefined;
    if (!rows) return;
    originalAssignments.current = rows.map((r) => ({ id: r.id, businessId: r.endCustomerBusinessId }));
    if (rows.length > 0) setTargetCustomerIds(rows.map((r) => r.endCustomerBusinessId));
  }, [existingAssignments]);

  const onTargetCustomerIdsChange = useCallback((ids: string[]) => {
    setTargetCustomerIds(ids);
  }, []);

  // Find the current ACTIVE version of the same scope (DEFAULT, or NAMED matched by name)
  // to compare against the draft being activated.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const activeRows: any[] = Array.isArray(activeList) ? activeList : ((activeList as any)?.rows ?? []);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const activeMatch: any = activeRows.find((p) =>
    form.scope === "NAMED" ? p.scope === "NAMED" && p.name === form.name : p.scope === "DEFAULT"
  );
  // Prefer the real current-active version; if none exists, before stays empty so all
  // fields render as "added" rather than comparing the draft against itself.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lo: any = activeMatch ?? {};
  // Human-readable formatting for the activation summary (decimal rate → "5%",
  // enum codes → Mongolian labels), so the confirm modal reads like a summary
  // rather than raw DB values.
  const CALC_LABELS: Record<string, string> = { PERCENTAGE: "Хувиар", FLAT: "Тогтмол дүн", TIERED: "Шатлалаар" };
  const REFUND_LABELS: Record<string, string> = { PRO_RATA: "Хувь тэнцүүлэн буцаах", NONE: "Буцаалтгүй", FULL_ONLY: "Зөвхөн бүтэн буцаалт" };
  const fmtPct = (r: unknown): string | null =>
    r == null || r === "" ? null : `${parseFloat((Number(r) * 100).toFixed(4))}%`;
  const fmtAmount = (a: unknown, cur?: string): string | null =>
    a == null || a === "" ? null : `${Number(a).toLocaleString("en-US")}${cur ? ` ${cur}` : ""}`;
  const diffBefore = activeMatch
    ? {
        calculationMethod: CALC_LABELS[lo.calculationMethod] ?? lo.calculationMethod,
        feeRate          : lo.calculationMethod === "PERCENTAGE" ? fmtPct(lo.feeRate) : null,
        flatFeeAmount    : lo.calculationMethod === "FLAT" ? fmtAmount(lo.flatFeeAmount, lo.feeCurrency) : null,
        refundPolicy     : REFUND_LABELS[lo.refundPolicy] ?? lo.refundPolicy,
        absorbPlatformFee: lo.absorbPlatformFee ? "Тийм" : "Үгүй",
        effectiveFrom    : lo.effectiveFrom ? String(lo.effectiveFrom).substring(0, 10) : null,
        effectiveUntil   : lo.effectiveUntil ? String(lo.effectiveUntil).substring(0, 10) : null
      }
    : {};
  const diffAfter = {
    calculationMethod: CALC_LABELS[form.calculationMethod] ?? form.calculationMethod,
    feeRate          : form.calculationMethod === "PERCENTAGE" ? fmtPct(form.feeRate) : null,
    flatFeeAmount    : form.calculationMethod === "FLAT" ? fmtAmount(form.flatFeeAmount, form.feeCurrency) : null,
    refundPolicy     : REFUND_LABELS[form.refundPolicy] ?? form.refundPolicy,
    absorbPlatformFee: form.absorbPlatformFee ? "Тийм" : "Үгүй",
    effectiveFrom    : effectiveFrom ?? null,
    effectiveUntil   : form.effectiveUntil ?? null
  };
  const scheduledNote = effectiveFrom && new Date(effectiveFrom) > new Date()
    ? `Ирээдүйн огноо тул товлогдоно (SCHEDULED, ${effectiveFrom}).`
    : "Одоо идэвхтэй (ACTIVE) болно.";

  // All this tenant's policies (for NAMED name-uniqueness check).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allPolicies: any[] = Array.isArray(allList) ? allList : ((allList as any)?.rows ?? []);

  // Client-side validation (spec §3.3 "Validation feedback").
  const errors = (() => {
    const e: { name?: string; feeRate?: string; minFeeAmount?: string; maxFeeAmount?: string; effectiveFrom?: string; effectiveUntil?: string; tierConfig?: string } = {};

    // NAMED scope requires a non-empty name.
    if (form.scope === "NAMED" && !form.name?.trim()) {
      e.name = "Нэртэй бодлогод нэр заавал шаардлагатай.";
    }

    // NAMED name must be unique among this tenant's NAMED policies (case-insensitive,
    // excluding the current draft and ARCHIVED). Mirrors the backend guard. Skipped
    // for amend drafts, which intentionally share their source policy's name.
    if (!isAmend && form.scope === "NAMED" && form.name?.trim()) {
      const wanted = form.name.trim().toLowerCase();
      const dup = allPolicies.some(
        (p) =>
          p.id !== id &&
          p.scope === "NAMED" &&
          p.status !== "ARCHIVED" &&
          (p.name ?? "").trim().toLowerCase() === wanted
      );
      if (dup) e.name = "Энэ нэртэй NAMED бодлого аль хэдийн байна.";
    }

    // Rate must be non-negative (no upper cap).
    if (form.calculationMethod === "PERCENTAGE" && form.feeRate != null && form.feeRate !== "" && Number(form.feeRate) < 0) {
      e.feeRate = "Хувь сөрөг байж болохгүй.";
    }

    // TIERED: mirror the backend activation gate (validateTiers) so the user
    // sees what blocks activation inline; drafts still save while incomplete.
    if (form.calculationMethod === "TIERED") {
      const tiers = Array.isArray(form.tierConfig) ? (form.tierConfig as FeePolicyTier[]) : [];
      if (tiers.length === 0) {
        e.tierConfig = "Дор хаяж нэг шатлал нэмнэ үү.";
      } else {
        for (let i = 0; i < tiers.length; i++) {
          const t = tiers[i];
          if (!t.rate || Number(t.rate) < 0 || Number(t.rate) >= 1) {
            e.tierConfig = `Шатлал ${i + 1}: хувийг 0–99.99% хооронд оруулна уу.`;
            break;
          }
          if (i === 0 && Number(t.fromAmount) !== 0) {
            e.tierConfig = "Эхний шатлал 0-оос эхлэх ёстой.";
            break;
          }
          if (i < tiers.length - 1) {
            if (t.toAmount == null || t.toAmount === "") {
              e.tierConfig = `Шатлал ${i + 1}: дээд дүн шаардлагатай (зөвхөн сүүлийн шатлал хязгааргүй байж болно).`;
              break;
            }
            if (Number(t.toAmount) <= Number(t.fromAmount)) {
              e.tierConfig = `Шатлал ${i + 1}: дээд дүн доод дүнгээс их байх ёстой.`;
              break;
            }
          }
        }
      }
    }

    // Min ≤ Max when both set.
    const min = form.minFeeAmount != null && form.minFeeAmount !== "" ? Number(form.minFeeAmount) : null;
    const max = form.maxFeeAmount != null && form.maxFeeAmount !== "" ? Number(form.maxFeeAmount) : null;
    if (min != null && max != null && min > max) {
      e.maxFeeAmount = "Дээд хязгаар нь доод хязгаараас бага байж болохгүй.";
    }

    // effectiveFrom not more than 24h in the past.
    if (effectiveFrom) {
      const ef = new Date(effectiveFrom).getTime();
      if (!Number.isNaN(ef) && ef < Date.now() - 24 * 60 * 60 * 1000) {
        e.effectiveFrom = "Эхлэх огноо 24 цагаас илүү өнгөрсөн байж болохгүй.";
      }
    }

    // effectiveUntil must be after effectiveFrom when both are set.
    if (form.effectiveUntil && effectiveFrom) {
      const until = new Date(form.effectiveUntil).getTime();
      const from = new Date(effectiveFrom).getTime();
      if (!Number.isNaN(until) && !Number.isNaN(from) && until <= from) {
        e.effectiveUntil = "Дуусах огноо эхлэх огнооноос хойш байх ёстой.";
      }
    }

    return e;
  })();
  const isValid = Object.keys(errors).length === 0;

  const handleSave = useCallback(async () => {
    // Manual save wins over any pending auto-save: cancel the debounce and clear
    // dirty so the two paths never double-fire.
    cancelAutoSave();
    setDirty(false);
    setBusy(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const body: any = { ...form, effectiveFrom };
      if (Array.isArray(form.tierConfig)) body.tierConfig = form.tierConfig;
      if (isNew) {
        const res = await run(() => TenantFeePolicyApi.createDraft(body));
        if (res.ok) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const newId = (res.value as any).id as string;
          // Carry the NAMED customer selection to the freshly-mounted edit view, where
          // it gets applied at activation (assignBulk requires an active/scheduled policy).
          if (form.scope === "NAMED" && targetCustomerIds.length) {
            sessionStorage.setItem(`fp-pending-customers:${newId}`, JSON.stringify(targetCustomerIds));
          }
          navigate(`/my-billing/fee-policy/${newId}?edit=1`);
        }
      } else {
        const res = await run(() => TenantFeePolicyApi.saveDraft(id!, { ...body, rowVersion: form.rowVersion! }));
        if (res.ok) navigate(`/my-billing/fee-policy/${id}`);
      }
    } catch (e: unknown) {
      Message.error(resolveBillingError(e));
    } finally {
      setBusy(false);
    }
  }, [cancelAutoSave, effectiveFrom, form, id, isNew, navigate, run, targetCustomerIds]);

  const handleActivate = async () => {
    if (!id || !form.rowVersion || !effectiveFrom) return;
    const rowVersion = form.rowVersion;
    setBusy(true);
    try {
      const res = await run(() => TenantFeePolicyApi.activate(
        id, rowVersion, new Date(effectiveFrom).toISOString(),
        form.effectiveUntil ? new Date(form.effectiveUntil).toISOString() : null
      ));
      if (res.ok) {
        // NAMED: the policy is now ACTIVE/SCHEDULED, so apply the inline customer
        // selection — assign newly added customers in bulk and unassign removed ones.
        // Best-effort: a failure here surfaces but doesn't undo the activation.
        if (form.scope === "NAMED") {
          const policyId = sourcePolicyId ?? id;
          const baseline = originalAssignments.current;
          const baselineIds = new Set(baseline.map((a) => a.businessId));
          const added = targetCustomerIds.filter((cid) => !baselineIds.has(cid));
          const removed = baseline.filter((a) => !targetCustomerIds.includes(a.businessId));
          try {
            if (added.length) {
              await TenantFeePolicyAssignmentApi.assignBulk({ feePolicyId: policyId!, customerBusinessIds: added });
            }
            for (const a of removed) {
              await TenantFeePolicyAssignmentApi.unassign(a.id);
            }
          } catch (e: unknown) {
            Message.error(resolveBillingError(e));
          }
          void mutate(`swr.my.fee-policy-assignments.${policyId}`);
        }
        // Refresh the list (the amend draft is now ARCHIVED → drops from the strip)
        // and the source view; land on the source policy for an amend, else the
        // freshly-activated policy.
        void mutate("swr.my.fee-policies.all");
        void mutate("swr.my.fee-policy-customers.counts");
        if (sourcePolicyId) void mutate(`swr.my.fee-policy.${sourcePolicyId}`);
        navigate(`/my-billing/fee-policy/${sourcePolicyId ?? id}`);
      }
    } catch (e: unknown) {
      Message.error(resolveBillingError(e));
    } finally {
      setBusy(false);
    }
  };

  // Performs one auto-save against the existing draft, refreshing the local
  // rowVersion + lastSavedAt from the response. A 409 surfaces the reload modal
  // (via run) and stops here — we never silently retry.
  const doAutoSave = useCallback(async () => {
    if (isNew || !id || !form.rowVersion) return;
    setAutoSaving(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const body: any = { ...form, effectiveFrom };
      if (Array.isArray(form.tierConfig)) body.tierConfig = form.tierConfig;
      const res = await run(() => TenantFeePolicyApi.saveDraft(id, { ...body, rowVersion: form.rowVersion! }));
      if (res.ok) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const saved: any = res.value;
        setForm((prev) => ({ ...prev, rowVersion: saved?.rowVersion ?? prev.rowVersion }));
        setLastSavedAt(saved?.updatedAt ? String(saved.updatedAt) : new Date().toISOString());
        setDirty(false);
      } else {
        // 409 — conflict modal is open; drop dirty so the debounce doesn't loop.
        setDirty(false);
      }
    } catch (e: unknown) {
      Message.error(resolveBillingError(e));
    } finally {
      setAutoSaving(false);
    }
  }, [id, isNew, form, effectiveFrom, run]);

  // Debounce: 2s after the last change, auto-save an existing dirty draft.
  // Re-runs on every form change (resetting the timer); cleanup cancels the
  // pending timer on unmount and before each new change.
  useEffect(() => {
    if (isNew || !id || !dirty || !form.rowVersion || conflict) return;
    autoSaveTimer.current = setTimeout(() => { void doAutoSave(); }, 2000);
    return () => cancelAutoSave();
  }, [form, effectiveFrom, dirty, isNew, id, conflict, doAutoSave, cancelAutoSave]);

  // Keyboard shortcuts (spec §8): Cmd/Ctrl+S = save draft, Esc = back.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === "s") {
        ev.preventDefault();
        if (!busy) void handleSave();
      } else if (ev.key === "Escape") {
        navigate("/my-billing/fee-policy");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, handleSave, navigate]);

  return (
    <Box p="lg">
      <Group justify="space-between" align="center" mb="xs">
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowBack size={16} />}
          onClick={() => navigate("/my-billing/fee-policy")}>
          Бодлогууд
        </Button>
      </Group>

      <Alert color="yellow" variant="light" icon={<IconPencil size={16} />} mb="md">
        Засах горим — өөрчлөлт оруулаад «Идэвхжүүлэх» эсвэл «Ноорог хадгалах».
      </Alert>

      {isAmend && (
        <Alert color="indigo" variant="light" icon={<IconHistory size={16} />} mb="md">
          Идэвхтэй бодлогын засвар — идэвхжүүлэхэд одоогийн хувилбарыг шинэ хувилбар (v{nextVersion})-аар солино.
        </Alert>
      )}

      {!isNew && (
        <DraftHeaderStrip
          scope={form.scope}
          name={form.name}
          // Amend → the version this draft becomes on activation (v{n+1}); otherwise
          // the loaded policy's current version. New/unactivated drafts (v0) show none.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          version={isAmend ? nextVersion : ((loaded as any)?.currentVersion as number | undefined)}
          rowVersion={form.rowVersion}
          lastSavedAt={lastSavedAt}
          saving={autoSaving}
        />
      )}

      <Stack gap="md">
        <PolicyForm
          value={form}
          onChange={updateField}
          readonly={false}
          errors={errors}
          isNew={isNew}
          effectiveFrom={effectiveFrom}
          onEffectiveFromChange={onEffectiveFromChange}
          trList={trList}
          lockName={isAmend}
          customers={customers}
          selectedCustomerIds={targetCustomerIds}
          onSelectedCustomerIdsChange={onTargetCustomerIdsChange}
        />

        <FeePreviewSimulator
          calculationMethod={form.calculationMethod}
          feeRate={form.feeRate}
          flatFeeAmount={form.flatFeeAmount}
          tierConfig={form.tierConfig as FeeTier[] | null | undefined}
          minFeeAmount={form.minFeeAmount}
          maxFeeAmount={form.maxFeeAmount}
          feeCurrency={form.feeCurrency ?? "MNT"}
          absorbPlatformFee={form.absorbPlatformFee}
          chargeEndCustomerFee={form.chargeEndCustomerFee}
        />

        {/* Edit footer (spec §3.1 / mockup D16): Болих · [saved-ago] · Ноорог хадгалах · Идэвхжүүлэх */}
        <Group justify="flex-end" align="center">
          <Button variant="default" onClick={() => navigate("/my-billing/fee-policy")} disabled={busy}>Болих</Button>
          {!isNew && (autoSaving
            ? <Text size="xs" c="dimmed">Хадгалж байна…</Text>
            : formatSavedAgo(lastSavedAt) && <Text size="xs" c="dimmed">{formatSavedAgo(lastSavedAt)}</Text>)}
          <PermissionGate code="BIL_011">
            <Button variant="default" onClick={handleSave} disabled={busy}>
              {isNew ? "Ноорог үүсгэх" : "Ноорог хадгалах"}
            </Button>
          </PermissionGate>
          {!isNew && (
            <PermissionGate code="BIL_011">
              <Button
                color="teal"
                onClick={() => setConfirmOpen(true)}
                disabled={busy || !form.rowVersion || !effectiveFrom || !isValid}>
                Идэвхжүүлэх
              </Button>
            </PermissionGate>
          )}
        </Group>
        {!isNew && !effectiveFrom && (
          <Text size="xs" c="dimmed" ta="right">
            Идэвхжүүлэхийн тулд «Эхлэх огноо»-г бөглөнө үү.
          </Text>
        )}
      </Stack>

      <ConflictReloadModal
        opened={conflict}
        onReload={() => { clearConflict(); if (id) mutate(`swr.my.fee-policy.${id}`); }}
      />

      <Modal opened={confirmOpen} onClose={() => setConfirmOpen(false)} title="Бодлого идэвхжүүлэх үү?" centered>
        <Stack gap="md">
          <DiffViewer
            before={diffBefore}
            after={diffAfter}
            labels={{
              calculationMethod: "Тооцооны арга",
              feeRate          : "Хувь",
              flatFeeAmount    : "Тогтмол дүн",
              refundPolicy     : "Буцаалт",
              absorbPlatformFee: "Платформ шимтгэл шингээх",
              effectiveFrom    : "Эхлэх огноо",
              effectiveUntil   : "Дуусах огноо"
            }}
          />
          {isAmend && (
            <Text size="sm" c="dimmed">
              Идэвхтэй бодлогын засвар: одоогийн хувилбарыг шинэ хувилбар (v{nextVersion})-аар солино. Бодлогын ID болон харилцагчийн оноолт хэвээр хадгалагдана.
            </Text>
          )}
          <Text size="sm" c="dimmed">{scheduledNote}</Text>
          <Text size="sm" c="dimmed">Платформын зөвшөөрөл шаардлагагүй.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmOpen(false)}>Болих</Button>
            <Button color="teal" loading={busy} onClick={async () => { setBusy(true); setConfirmOpen(false); await handleActivate(); }}>
              Идэвхжүүлэх
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default FeePolicyDraftPage;
